import React, {Component} from 'react';
import PropTypes from 'prop-types';
import appConfig from 'config/app.config';
import dayjs from 'dayjs';
import {
	createNewPlayerModule,
	getPlayerModuleData,
	getPlayerModulesData,
	getPlayerModuleSessionData,
	startNewSession, 
	getLastPlayedTaskId, 
} from 'helpers/module-helper';
import {getTimeSinceLastActivity} from 'helpers/time-helper';
import {getModuleMaxPoints, getNumberOfFilledStars} from 'helpers/points-helper';
import {errorUiTexts} from 'data/ui-texts/error-ui-texts';
import {modulesData} from 'data/modules-data';
import Loading from 'components/loading/loading';
import Module from './module';
import Gameover from '../gameover/gameover';
import ImageLoader from 'components/ui/image-loader/image-loader';

class ModuleController extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoading: true,
			isStartingNewSession: false,
			taskId: null,
			feedbackData: null,
			popupData: null,
			queuedEffects: [],
			streakType: 'error', // error / success
			streakValue: 0,
			loggedTime: 0,
			timestamp: Date.now()
		};
		this.timeout = null;
	}

	/**
	 * Component mounted
	 */
	componentDidMount = () => {
		/* Get player modules data (all modules) */
		let playerModulesData = getPlayerModulesData(this.props.playerData);

		/* Get player data for this module */
		let moduleIndex = playerModulesData.findIndex((m) => {return m.moduleId === this.props.moduleId;});
	
		if (moduleIndex < 0) {
			/* Player has just started first session in module, create player module data */
			playerModulesData.push(createNewPlayerModule(this.props.moduleId));
			this.props.updatePlayerData({modules: playerModulesData}).then(() => {
				/* Go to first task in module, automatically sets isLoading to false */
				this.handleGoToTask(playerModulesData[playerModulesData.length - 1].sessions[0].currentTaskId);
			});
		} else {
			/* Player has alrady started module, go to last played task, automatically sets isLoading to false */
			const taskId = getLastPlayedTaskId(this.props.moduleId, this.props.playerData);
			if (taskId) this.handleGoToTask(taskId);
		}
	};


	/**
	 * Component will unmount
	 */
	componentWillUnmount = () => {		
		/* Clear timeout */
		if (this.timeout) clearTimeout(this.timeout);
	};

	/**
	 * Update logged time
	 * @param {bool} resetLoggedTime
	 */
	updateLoggedTime = (resetLoggedTime = false) => {
		/* Get number of miliseconds since last update */
		const miliseconds = getTimeSinceLastActivity(this.state.timestamp);
		const newLoggedTime = this.state.loggedTime + miliseconds;

		this.setState({loggedTime: (resetLoggedTime ? 0 : newLoggedTime), timestamp: Date.now()});
		return newLoggedTime;
	};
	

	/**
	 * Update current task id for module in player data
	 */
	updateCurrentTaskId = (taskId) => {
		if (!taskId) return;

		/* Get player modules data */
		let playerModulesData = getPlayerModulesData(this.props.playerData);

		/* Get module index in player data */
		const moduleIndex = playerModulesData.findIndex((m) => {return m.moduleId === this.props.moduleId;});

		/* Get module session */
		let moduleSession = getPlayerModuleSessionData(this.props.playerData, this.props.moduleId);
		if (moduleSession) {
			moduleSession.currentTaskId = taskId;
			playerModulesData[moduleIndex].sessions[playerModulesData[moduleIndex].sessions.length - 1] = moduleSession;
			this.props.updatePlayerData({modules: playerModulesData});	
		} else {
			console.error('This should not happen!');
		}
	};

	/**
	 * Go to module task
	 * @param {number} taskId 
	 */
	handleGoToTask = (taskId) => {
		/* Go to task */
		this.setState({taskId, isLoading: false}, () => {
			/* Scroll to page top */
			this.props.scrollToTop();

			/* Get module and module task data */
			const moduleData = modulesData.find((m) => {return m.id === this.props.moduleId;});

			/* Get task data */
			const taskData = (moduleData 
				? moduleData.tasks.find((task) => {return task.id === this.state.taskId;})
				: null
			);

			/* Update background */
			let newBackgroundId = moduleData.background;
			let newBackgroundModifier = null;
				
			if (taskData && taskData.background) {
				newBackgroundId = taskData.background;
			}
			if (taskData && taskData.backgroundModifier) {
				newBackgroundModifier = taskData.backgroundModifier;
			}
			this.props.setBackground(this.props.languageId, newBackgroundId, newBackgroundModifier);

			/* Update current task in player data */
			this.updateCurrentTaskId(taskId);
		});
		
	};

	/**
	 * Handle instant task effects 
	 * They are triggered during a task (e.g. by selecting an option in multiple choice)
	 * @param {array} effects 
	 */
	handleInstantTaskEffects = (effects) => {
		/* Get queued effects */
		let queuedEffects = (this.state.queuedEffects && this.state.queuedEffects.length > 0 
			? [...this.state.queuedEffects] 
			: []
		);

		/* Get streak data */
		let newStreakType = this.state.streakType;
		let newStreakValue = this.state.streakValue;

		/* ShakeScreen flag */
		let shakeScreen = false;

		/* Loop over effects */
		effects.forEach((effect) => {
			if (
				(effect.type === 'feedback' && effect.feedback) ||
				(effect.type === 'popup' && effect.popup)
			) {
				/* Feedback / popup */
				queuedEffects.push(effect);
			}	else if (effect.type === 'shake-screen') {
				shakeScreen = true;
			}			
		});

		/* Shake screen */
		if (shakeScreen) {
			this.props.handleShakeScreen();
		}
	
		if (queuedEffects.length > 0) {
			/* Update streak data & start showing queued instant effects */
			this.setState({queuedEffects, streakType: newStreakType, streakValue: newStreakValue}, () => {
				this.processQueuedEffects(false);
			});
		} else {
			/* Update streak data */
			this.setState({streakType: newStreakType, streakValue: newStreakValue});
		}
	};

	/**
	 * Process queued instant effects
	 */
	processQueuedEffects = (delayNextEffect = true) => {
		/* Close all effects */
		this.setState({feedbackData: null, popupData: null}, () => {
			let queuedEffects = [...this.state.queuedEffects];
			if (queuedEffects.length > 0) {
				/* Display next effect */
				if (
					queuedEffects[0].type === 'feedback' ||
					queuedEffects[0].type === 'popup'
				) {
					/* Feedback or popup */
					const newQueuedEffects = queuedEffects.slice(1);
					const feedbackData = (queuedEffects[0].type === 'feedback' ? queuedEffects[0].feedback : null);
					const popupData = (queuedEffects[0].type === 'popup' ? queuedEffects[0].popup : null);

					const delay = (delayNextEffect 
						? (queuedEffects[0].type === 'feedback' ? appConfig.feedbackDelay : appConfig.popupDelay)
						: 0
					);
					if (this.timeout) clearTimeout(this.timeout);
					this.timeout = setTimeout(() => {
						this.setState({feedbackData, popupData, queuedEffects: newQueuedEffects});
					}, delay);	
				}
			}
		});
	};

	/**
	 * Handle a completed task
	 * @param {string} type 
	 * @param {number} points 
	 * @param {number} errors 
	 * @param {*object} taskData 
	 */
	handleCompleteTask = (type, points, errors, effects, taskData) => {	
		/* Get logged time, reset */
		const loggedTime = this.updateLoggedTime(true);

		/* Get streak data */ 
		let newStreakType = this.state.streakType;
		let newStreakValue = this.state.streakValue;

		/* Get task data */	
		const moduleData = modulesData.find((m) => {return m.id === this.props.moduleId;});
		const taskId = (moduleData && moduleData.tasks.some((task) => {return task.id === this.state.taskId;})
			? moduleData.tasks.find((task) => {return task.id === this.state.taskId;}).taskId
			: null
		);
		if (!taskId) {
			console.error('Completing task - but no task data found. Should not happen!');
			console.error('Task id: ', this.state.taskId);
			return;
		}

		/* Get player data */
		let playerModulesData = getPlayerModulesData(this.props.playerData);
		const moduleIndex = playerModulesData.findIndex((m) => {return m.moduleId === this.props.moduleId;});
		let playerSessionData = getPlayerModuleSessionData(this.props.playerData, this.props.moduleId);

		if (!playerSessionData) {
			console.error('Completing task - but no module / session found in database. Should not happen!');
			return;
		}

		/* Update logged time for session */
		if (!playerSessionData.hasOwnProperty('milisecondsPlayed')) playerSessionData.milisecondsPlayed = 0;
		playerSessionData.milisecondsPlayed = playerSessionData.milisecondsPlayed + loggedTime;	

		/* Prepare array of effects to show */
		let queuedEffects = [];

		/* Handle effects */
		effects.forEach((effect) => {
			if (
				(effect.type === 'feedback' && effect.feedback) ||
				(effect.type === 'popup' && effect.popup)
			) {
				/* Feedback / popup */
				queuedEffects.push(effect);
			}
		});

		/* Update total points in module */
		playerSessionData.points += points;

		console.log('task points: ', points);
		/* Add completed task to module */
		let taskObj = Object.assign({}, {
			isCompleted: true,
			taskId: taskId,
			type: type,
			points: points,
			errors: errors
		}, (taskData ? taskData : {}));
		if (!playerSessionData.tasks) playerSessionData.tasks = [];
		playerSessionData.tasks.push(taskObj);

		/* Update player data */
		playerModulesData[moduleIndex].sessions[playerModulesData[moduleIndex].sessions.length - 1] = playerSessionData;
		this.props.updatePlayerData({modules: playerModulesData});

		if (queuedEffects.length > 0) {
			/* Update streak data & start showing queued effects */
			this.setState({queuedEffects, streakType: newStreakType, streakValue: newStreakValue}, () => {
				this.processQueuedEffects(false);
			});
		} else {
			/* Update streak data */
			this.setState({streakType: newStreakType, streakValue: newStreakValue});
		}
	};

	/**
	 * Complete module session
	 */
	handleCompleteModuleSession = () => {
		/* Mark session as completed */
		const playerModulesData = getPlayerModulesData(this.props.playerData);
		const moduleIndex = playerModulesData.findIndex((m) => {return m.moduleId === this.props.moduleId;});
		const playerSessionData = getPlayerModuleSessionData(this.props.playerData, this.props.moduleId);
		playerSessionData.isCompleted = true;
		playerModulesData[moduleIndex].sessions[playerModulesData[moduleIndex].sessions.length - 1] = playerSessionData;

		/* Check if number of required stars achieved */
		const moduleData = modulesData.find((m) => {return m.id === this.props.moduleId;});
		const numberOfFilledStars = getNumberOfFilledStars(
			playerSessionData.points,
			getModuleMaxPoints(this.props.moduleId)
		);
		if (numberOfFilledStars >= moduleData.minStars && !playerModulesData[moduleIndex].completedDate) {
			playerModulesData[moduleIndex].completedDate = dayjs(new Date()).format('YYYY-MM-DD');
		}

		/* Prepare player data updates */
		const playerDataUpdates = {modules: playerModulesData};

		/* Check of all modules have been completed */
		if (!this.props.playerData.completed) {
			const allModulesAreCompleted = modulesData.every((moduleData) => {
				const playerModuleData = getPlayerModuleData(moduleData.id, this.props.playerData);
				if (playerModuleData && playerModuleData.completedDate) {
					return true;
				}
				return false;
			});
			if (allModulesAreCompleted) {
				playerDataUpdates.completed = dayjs(new Date()).format('YYYY-MM-DD');
			}
		}

		/* Update player data */
		this.props.updatePlayerData(playerDataUpdates);
	};

	/**
	 * Handle reset module
	 */
	handleStartNewSession = () => {
		this.setState({isStartingNewSession: true, loggedTime: 0, timestamp: Date.now()}, () => {
			const playerModulesData = startNewSession(this.props.moduleId, this.props.playerData);
			if (playerModulesData) {
				/* Reset module */
				this.props.updatePlayerData({modules: playerModulesData}).then(() => {
					const moduleData = modulesData.find((m) => {return m.id === this.props.moduleId;});
					const taskId = (moduleData && moduleData.tasks && moduleData.tasks.length > 0 
						? moduleData.tasks[0].id
						: null
					);

					/* Get task data */
					const taskData = (moduleData 
						? moduleData.tasks.find((task) => {return task.id === taskId;})
						: null
					);
			
					/* Update background */
					let newBackgroundId = moduleData.background;
					let newBackgroundModifier = null;
					
					if (taskData && taskData.background) {
						newBackgroundId = taskData.background;
						newBackgroundModifier = taskData.backgroundModifier;
					}
					this.props.setBackground(this.props.languageId, newBackgroundId, newBackgroundModifier);

					/* Hide result, go to first task in module */
					this.setState({
						isStartingNewSession: false,
						taskId: taskId,
					});
				});
			} else {
				/* Module not found (should not happen) */
				this.setState({isStartingNewSession: false});
			}
		});
	};


	/**
	 * Render component
	 */
	render() {
		/* Loading */
		if (this.state.isLoading) {
			return (
				<Loading type="loading-module" languageId={this.props.languageId} />
			);
		};

		/* Get module and module task data */
		const moduleData = modulesData.find((m) => {return m.id === this.props.moduleId;});

		/* Module not found */
		if (!moduleData) {
			return <div>{'Unknown module id'}: {this.props.moduleId}</div>;
		}

		const playerSessionData = getPlayerModuleSessionData(this.props.playerData, this.props.moduleId);
		if (playerSessionData.isCompleted) {
			return (
				<Gameover 
					languageId={this.props.languageId}
					moduleId={moduleData.id}
					deviceInfo={this.props.deviceInfo}
					playerData={this.props.playerData}
					handleGoToGamePage={this.props.handleGoToGamePage}
					resetPlayerData={this.props.resetPlayerData}
					startNewSessionInModule={this.props.startNewSessionInModule}
					handleGoToTask={this.handleGoToTask}
				/>
			);
		}

		/* Get task data */
		const taskData = (moduleData 
			? moduleData.tasks.find((task) => {return task.id === this.state.taskId;})
			: null
		);
		if (!taskData) {
			console.error(this.props.moduleId);
			console.error(this.state.taskId);
			return (
				<div>{errorUiTexts.unknownModuleTaskId}: {this.state.taskId}</div>
			);
		}
		
		/* Render component */
		return (
			<>
				<Module 
					hasQueuedEffects={this.state.queuedEffects.length > 0 || this.state.queuedEffects.length > 0}
					languageId={this.props.languageId}
					backgroundStatus={this.props.backgroundStatus}
					moduleData={moduleData} 
					taskData={taskData}
					playerData={this.props.playerData}
					feedbackData={this.state.feedbackData}
					popupData={this.state.popupData}
					deviceInfo={this.props.deviceInfo}
					updateLoggedTime={this.updateLoggedTime}
					handleGoToTask={this.handleGoToTask}
					handleInstantTaskEffects={this.handleInstantTaskEffects}
					handleCompleteTask={this.handleCompleteTask}
					handleGoToGamePage={this.props.handleGoToGamePage}
					processQueuedEffects={this.processQueuedEffects}
					handleCompleteModuleSession={this.handleCompleteModuleSession}
				/>
				<ImageLoader type={'module-' + this.props.moduleId} />
			</>
		);
	}
}

ModuleController.propTypes = {
	languageId: PropTypes.string.isRequired,
	backgroundStatus: PropTypes.string.isRequired,
	moduleId: PropTypes.string.isRequired,
	playerData: PropTypes.object.isRequired,
	deviceInfo: PropTypes.object.isRequired,
	handleGoToGamePage: PropTypes.func.isRequired,
	updatePlayerData: PropTypes.func.isRequired,
	scrollToTop: PropTypes.func.isRequired,
	setBackground: PropTypes.func.isRequired,
	handleShakeScreen: PropTypes.func.isRequired,
	resetPlayerData: PropTypes.func.isRequired,
	startNewSessionInModule: PropTypes.func.isRequired
};

export default ModuleController;
